import React, { useEffect, useState, useRef } from 'react';
import {
  adminTicketApi,
  supportApi,
  TicketFilters,
  UpdateTicketRequest,
  AddTicketMessageRequest,
  AdminTicket,
  CreateManualTicketRequest
} from '../services/supportApi';
import { FiMessageSquare, FiSearch, FiDownload, FiFileText, FiFile, FiPlus } from 'react-icons/fi';
import CannedResponsePicker from './CannedResponsePicker';
import { appendCannedResponseContent, useCannedResponseShortcuts } from '../hooks/useCannedResponseShortcuts';
import './SupportTickets.css';

interface TicketStats {
  totalTickets: number;
  openTickets: number;
  inProgressTickets: number;
  resolvedTickets: number;
  closedTickets: number;
  unassignedTickets?: number;
  averageResponseTime?: number;
  averageResolutionTime?: number;
  slaResponseBreached?: number;
  slaResolutionBreached?: number;
  ticketsByCategory?: Record<string, number>;
  ticketsByPriority?: Record<string, number>;
}

const SupportTickets: React.FC = () => {
  // ─── Helpers to handle different backend response shapes ───────────────────
  /**
   * The backend may return sender info in different shapes:
   *   { sender: { id, name } }       — ideal nested object
   *   { senderId, senderName }        — flat fields
   *   { userId, userName }            — legacy flat fields
   *   { admin: { id, name } }         — admin sender
   * We also label "ADMIN" senders so the admin knows who sent what.
   */
  const resolveSenderName = (message: any): string => {
    // Nested sender object
    if (message.sender?.name) return message.sender.name;
    // Flat sender fields
    if (message.senderName) return message.senderName;
    // Flat user fields
    if (message.userName) return message.userName;
    // Admin nested
    if (message.admin?.name) return `${message.admin.name} (Admin)`;
    // Fall back to role label if we know it's admin
    if (message.senderRole === 'ADMIN' || message.isAdminMessage) return 'Admin';
    if (message.senderRole === 'USER') return 'User';
    // Use IDs if at least that is available
    const id = message.sender?.id ?? message.senderId ?? message.userId;
    if (id) return `User #${id}`;
    return 'Unknown Sender';
  };

  /**
   * Resolve the display name + phone for a ticket's user.
   * Backend can return: { user: { name, phoneNumber } } or flat { userName, userPhone } etc.
   */
  const resolveTicketUser = (ticket: any): { name: string; phone: string } => {
    const name =
      ticket.user?.name ||
      ticket.userName ||
      ticket.userFullName ||
      (ticket.userId ? `User #${ticket.userId}` : null) ||
      'Unknown User';
    const phone =
      ticket.user?.phoneNumber ||
      ticket.userPhone ||
      ticket.userPhoneNumber ||
      'N/A';
    return { name, phone };
  };

  /**
   * Determine CSS class for a message bubble:
   * Admin messages → right-aligned "admin-message"
   * User messages  → left-aligned  "user-message"
   */
  const getMessageClass = (message: any, ticket: AdminTicket): string => {
    // If sender is the assigned admin
    if (message.sender?.id && ticket.assignedAdminId && message.sender.id === ticket.assignedAdminId)
      return 'admin-message';
    // Flat role field
    if (message.senderRole === 'ADMIN' || message.isAdminMessage || message.admin) return 'admin-message';
    return 'user-message';
  };

  const [tickets, setTickets] = useState<AdminTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<AdminTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [filter, setFilter] = useState<string>('ALL');
  const [priorityFilter, setPriorityFilter] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [newMessage, setNewMessage] = useState<string>('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [showCannedResponsePicker, setShowCannedResponsePicker] = useState(false);
  const ticketReplyInputRef = useRef<HTMLTextAreaElement>(null);

  useCannedResponseShortcuts(newMessage, setNewMessage);

  // Manual Ticket State
  const [showManualModal, setShowManualModal] = useState(false);
  const [creatingManual, setCreatingManual] = useState(false);
  const [manualTicketData, setManualTicketData] = useState<CreateManualTicketRequest>({
    subject: '',
    description: '',
    phoneNumber: '',
    userName: '',
    userId: undefined,
    priority: 'MEDIUM',
    attachmentUrls: []
  });

  // Load stats and tickets on mount (without filters)
  useEffect(() => {
    console.log('🚀 Component mounted - Loading initial data...');
    loadStats();
    // Load tickets without any filters on initial mount
    loadTickets();
  }, []);

  // Reload tickets when filters change
  useEffect(() => {
    console.log('🔄 Filters changed - Reloading tickets...');
    loadTickets();
  }, [filter, priorityFilter]);

  // Debug: Log when filters change
  useEffect(() => {
    console.log('🔍 Filters changed:', { filter, priorityFilter });
  }, [filter, priorityFilter]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const filters: TicketFilters = {};
      if (filter !== 'ALL') {
        filters.status = filter;
      }
      if (priorityFilter !== 'ALL') {
        filters.priority = priorityFilter;
      }
      console.log('🔍 Loading tickets with filters:', filters);

      const ticketsData = await adminTicketApi.getAll(filters);
      console.log('✅ Tickets loaded:', ticketsData.length);

      if (ticketsData.length === 0 && stats && stats.totalTickets > 0) {
        console.warn('⚠️ WARNING: Stats show', stats.totalTickets, 'tickets but API returned 0.');
        console.warn('⚠️ Current filter:', filter, 'Priority:', priorityFilter);
      }

      setTickets(ticketsData);
    } catch (error: any) {
      console.error('❌ Failed to load tickets:', error);
      alert(`Failed to load tickets: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await adminTicketApi.getStats();
      console.log('📊 Stats loaded:', statsData);
      // Map TicketStatistics to TicketStats format
      const mappedStats: TicketStats = {
        ...statsData,
        unassignedTickets: 0, // Default value if not provided
        ticketsByPriority: statsData.byPriority
      };
      setStats(mappedStats);
    } catch (error: any) {
      console.error('❌ Failed to load ticket stats:', error);
    }
  };

  const loadTicketDetails = async (ticketId: number) => {
    try {
      const ticketData = await adminTicketApi.getById(ticketId);
      console.log('📋 Ticket details loaded:', ticketData);
      setSelectedTicket(ticketData);
    } catch (error: any) {
      console.error('❌ Failed to load ticket details:', error);
      alert('Failed to load ticket details. Please try again.');
    }
  };

  const handleTicketClick = (ticket: AdminTicket) => {
    loadTicketDetails(ticket.id);
  };

  const handleInsertCannedResponse = (content: string) => {
    setNewMessage(prev => appendCannedResponseContent(prev, content));
    setTimeout(() => ticketReplyInputRef.current?.focus(), 100);
  };

  const handleSendMessage = async () => {
    if (!selectedTicket || !newMessage.trim()) return;

    try {
      setSendingMessage(true);
      const messageData: AddTicketMessageRequest = {
        message: newMessage,
        isInternal: false,
        internal: false,
        attachmentUrls: []
      };
      await adminTicketApi.addMessage(selectedTicket.id, messageData);
      setNewMessage('');
      // Reload ticket details to get updated messages
      await loadTicketDetails(selectedTicket.id);
      await loadTickets(); // Refresh list to update unread counts
    } catch (error: any) {
      console.error('❌ Failed to send message:', error);
      alert(`Failed to send message: ${error.response?.data?.error || error.message}`);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleUpdateStatus = async (ticketId: number, newStatus: string, newPriority?: string, assignedAdminId?: number | null) => {
    try {
      console.log('🔄 Updating ticket', ticketId, 'status:', newStatus, 'priority:', newPriority, 'assignedAdminId:', assignedAdminId);
      const updateData: UpdateTicketRequest = {
        status: newStatus,
        priority: newPriority,
        assignedAdminId: assignedAdminId
      };
      await adminTicketApi.update(ticketId, updateData);
      await loadTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        await loadTicketDetails(ticketId);
      }
    } catch (error: any) {
      console.error('❌ Failed to update ticket:', error);
      alert(`Failed to update ticket: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCloseTicket = async (ticketId: number) => {
    if (!window.confirm('Are you sure you want to close this ticket?')) {
      return;
    }

    try {
      console.log('🔒 Closing ticket:', ticketId);
      await adminTicketApi.close(ticketId);
      await loadTickets();
      if (selectedTicket && selectedTicket.id === ticketId) {
        await loadTicketDetails(ticketId);
      }
    } catch (error: any) {
      console.error('❌ Failed to close ticket:', error);
      alert(`Failed to close ticket: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleExportPdf = async (ticketId: number) => {
    try {
      const blob = await adminTicketApi.exportPdf(ticketId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticketId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('❌ Failed to export PDF:', error);
      alert(`Failed to export PDF: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleExportExcel = async (ticketId: number) => {
    try {
      const blob = await adminTicketApi.exportExcel(ticketId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ticket-${ticketId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('❌ Failed to export Excel:', error);
      alert(`Failed to export Excel: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleExportMultiple = async (format: 'pdf' | 'excel') => {
    if (tickets.length === 0) {
      alert('No tickets to export');
      return;
    }

    const selectedIds = selectedTicket ? [selectedTicket.id] : tickets.map(t => t.id);

    try {
      const blob = format === 'pdf'
        ? await adminTicketApi.exportPdfMultiple(selectedIds)
        : await adminTicketApi.exportExcelMultiple(selectedIds);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tickets-${new Date().toISOString()}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error(`❌ Failed to export ${format}:`, error);
      alert(`Failed to export ${format}: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleCreateManualTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualTicketData.subject || !manualTicketData.description) {
      alert('Subject and Description are required');
      return;
    }

    if (!manualTicketData.userId && !manualTicketData.phoneNumber) {
      alert('Either User ID or Phone Number is required');
      return;
    }

    try {
      setCreatingManual(true);
      const newTicket = await adminTicketApi.createManual(manualTicketData);
      console.log('✅ Manual ticket created:', newTicket);

      setShowManualModal(false);
      setManualTicketData({
        subject: '',
        description: '',
        phoneNumber: '',
        userName: '',
        userId: undefined,
        priority: 'MEDIUM',
        attachmentUrls: []
      });

      await loadTickets();
      await loadStats();
      handleTicketClick(newTicket);
      alert('Ticket created successfully!');
    } catch (error: any) {
      console.error('❌ Failed to create manual ticket:', error);
      alert(`Failed to create ticket: ${error.response?.data?.error || error.response?.data?.message || error.message}`);
    } finally {
      setCreatingManual(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OPEN': return '#ff9800';
      case 'IN_PROGRESS': return '#2196f3';
      case 'WAITING_FOR_CUSTOMER': return '#ffc107';
      case 'ESCALATED': return '#f44336';
      case 'RESOLVED': return '#4caf50';
      case 'CLOSED': return '#9e9e9e';
      default: return '#666';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'CRITICAL':
      case 'URGENT': return '#f44336';
      case 'HIGH': return '#ff9800';
      case 'MEDIUM': return '#ffc107';
      case 'LOW': return '#4caf50';
      default: return '#666';
    }
  };

  const filteredTickets = tickets
    .filter(ticket => ticket != null) // Filter out any null/undefined tickets first
    .filter(ticket => {
      if (!searchTerm) return true;
      const search = searchTerm.toLowerCase();
      return (
        ticket.subject?.toLowerCase().includes(search) ||
        (ticket.user?.name && ticket.user.name.toLowerCase().includes(search)) ||
        (ticket.user?.phoneNumber && ticket.user.phoneNumber.includes(search)) ||
        ticket.id?.toString().includes(search)
      );
    });

  if (loading && tickets.length === 0) {
    return <div className="loading">Loading tickets...</div>;
  }

  return (
    <div className="support-tickets-management">
      <div className="section-header">
        <h2>
          {React.createElement(FiMessageSquare as any, { className: "section-icon" })}
          Support Tickets Management
        </h2>
        <div className="header-actions">
          <button
            className="btn-create-manual"
            onClick={() => setShowManualModal(true)}
            title="Create ticket manually for a user"
          >
            {React.createElement(FiPlus as any)} Create Manual Ticket
          </button>
          <button
            className="btn-export-multiple"
            onClick={() => handleExportMultiple('pdf')}
            title="Export all tickets as PDF"
          >
            {React.createElement(FiDownload as any)} Export All PDF
          </button>
          <button
            className="btn-export-multiple"
            onClick={() => handleExportMultiple('excel')}
            title="Export all tickets as Excel"
          >
            {React.createElement(FiDownload as any)} Export All Excel
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="ticket-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.totalTickets}</div>
            <div className="stat-label">Total Tickets</div>
          </div>
          <div className="stat-card open">
            <div className="stat-value">{stats.openTickets}</div>
            <div className="stat-label">Open</div>
          </div>
          <div className="stat-card in-progress">
            <div className="stat-value">{stats.inProgressTickets}</div>
            <div className="stat-label">In Progress</div>
          </div>
          <div className="stat-card resolved">
            <div className="stat-value">{stats.resolvedTickets}</div>
            <div className="stat-label">Resolved</div>
          </div>
          <div className="stat-card unassigned">
            <div className="stat-value">{stats.unassignedTickets}</div>
            <div className="stat-label">Unassigned</div>
          </div>
          {stats.averageResponseTime !== undefined && (
            <div className="stat-card">
              <div className="stat-value">{stats.averageResponseTime.toFixed(1)}h</div>
              <div className="stat-label">Avg Response Time</div>
            </div>
          )}
          {stats.averageResolutionTime !== undefined && (
            <div className="stat-card">
              <div className="stat-value">{stats.averageResolutionTime.toFixed(1)}h</div>
              <div className="stat-label">Avg Resolution Time</div>
            </div>
          )}
          {stats.slaResponseBreached !== undefined && stats.slaResponseBreached > 0 && (
            <div className="stat-card sla-breach">
              <div className="stat-value">{stats.slaResponseBreached}</div>
              <div className="stat-label">SLA Response Breached</div>
            </div>
          )}
          {stats.slaResolutionBreached !== undefined && stats.slaResolutionBreached > 0 && (
            <div className="stat-card sla-breach">
              <div className="stat-value">{stats.slaResolutionBreached}</div>
              <div className="stat-label">SLA Resolution Breached</div>
            </div>
          )}
        </div>
      )}

      {/* ─── FULL-PAGE DETAIL VIEW ─────────────────────────────────── */}
      {selectedTicket ? (
        <div className="ticket-detail-page">
          {/* Detail Header */}
          <div className="ticket-detail-header">
            <div className="detail-header-left">
              <button className="btn-back" onClick={() => setSelectedTicket(null)}>
                ← Back to Tickets
              </button>
              <div>
                <h3>Ticket #{selectedTicket.id}</h3>
                <div className="ticket-detail-meta">
                  <span className="ticket-user-info">
                    {(() => { const u = resolveTicketUser(selectedTicket); return `${u.name} (${u.phone})`; })()}
                  </span>
                  <span className="ticket-category">{selectedTicket.category || selectedTicket.requestType?.nameEn || 'General'}</span>
                </div>
              </div>
            </div>
            <div className="ticket-actions">
              <select
                value={selectedTicket.status}
                onChange={(e) => handleUpdateStatus(selectedTicket.id, e.target.value)}
                className="status-select"
              >
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="WAITING_FOR_CUSTOMER">Waiting for Customer</option>
                <option value="ESCALATED">Escalated</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
              </select>
              <select
                value={selectedTicket.priority}
                onChange={(e) => handleUpdateStatus(selectedTicket.id, selectedTicket.status, e.target.value)}
                className="priority-select"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
                <option value="CRITICAL">Critical</option>
              </select>
              <div className="export-buttons" style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-export" onClick={() => handleExportPdf(selectedTicket.id)} title="Export as PDF">
                  {React.createElement(FiFileText as any)} PDF
                </button>
                <button className="btn-export" onClick={() => handleExportExcel(selectedTicket.id)} title="Export as Excel">
                  {React.createElement(FiFile as any)} Excel
                </button>
              </div>
              {selectedTicket.status !== 'CLOSED' && (
                <button className="btn-close" onClick={() => handleCloseTicket(selectedTicket.id)}>
                  Close Ticket
                </button>
              )}
            </div>
          </div>

          {/* Ticket Info Summary */}
          <div className="ticket-info-card">
            <div className="ticket-info-row">
              <span className="info-label">Subject:</span>
              <span className="info-value">{selectedTicket.subject}</span>
            </div>
            <div className="ticket-info-row">
              <span className="info-label">Description:</span>
              <span className="info-value desc-text">{selectedTicket.description}</span>
            </div>
          </div>

          {/* Chat Messages */}
          <div className="ticket-messages">
            <h4>Messages ({selectedTicket.messages?.length || 0})</h4>
            <div className="messages-list">
              {selectedTicket.messages?.map(message => (
                <div
                  key={message.id}
                  className={`message ${getMessageClass(message, selectedTicket)}`}
                >
                  <div className="message-header">
                    <span className="message-sender">{resolveSenderName(message)}</span>
                    <span className="message-time">
                      {new Date(message.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="message-content">{message.message}</div>
                  {message.internal && (
                    <span className="internal-message-badge">Internal</span>
                  )}
                  {message.attachments && message.attachments.length > 0 ? (
                    <div className="message-attachments">
                      {message.attachments.map((url, idx) => {
                        const isImage = url.match(/\.(jpeg|jpg|gif|png|webp)($|\?)/i);
                        const fullUrl = url.startsWith('http') ? url : `${supportApi.getBaseUrl().replace('/api', '')}${url}`;
                        return (
                          <div key={idx} className="attachment-item">
                            {isImage ? (
                              <a href={fullUrl} target="_blank" rel="noopener noreferrer" title="Click to view image">
                                <img src={fullUrl} alt={`Attachment ${idx + 1}`} className="attachment-image" />
                              </a>
                            ) : (
                              <a href={fullUrl} target="_blank" rel="noopener noreferrer" className="attachment-link" title="Click to download file">
                                {React.createElement(FiFile as any, { className: "attachment-icon" })}
                                Attachment {idx + 1}
                              </a>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="message-attachments empty">
                      <span className="no-attachments-text">No attachments</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Reply Input */}
          {selectedTicket.status !== 'CLOSED' && (
            <div className="message-input-section">
              <div className="reply-toolbar">
                <button
                  type="button"
                  className="btn-canned-reply"
                  onClick={() => setShowCannedResponsePicker(true)}
                  title="Insert canned response (type /shortcut + space)"
                >
                  {React.createElement(FiMessageSquare as any, { className: 'btn-canned-reply-icon' })}
                  <span>Canned</span>
                </button>
              </div>
              <textarea
                ref={ticketReplyInputRef}
                className="message-input"
                placeholder="Type /shortcut + space or your reply..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                rows={4}
              />
              <button
                className="btn-send"
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sendingMessage}
              >
                {sendingMessage ? 'Sending...' : 'Send Message'}
              </button>
              <CannedResponsePicker
                anchorEl={ticketReplyInputRef.current}
                open={showCannedResponsePicker}
                onClose={() => setShowCannedResponsePicker(false)}
                onSelect={handleInsertCannedResponse}
                placement="top"
              />
            </div>
          )}
        </div>
      ) : (
        /* ─── FULL-PAGE LIST VIEW ─────────────────────────────────── */
        <div className="tickets-list-page">
          <div className="tickets-list">
            <div className="filters-section">
              <div className="search-box">
                {React.createElement(FiSearch as any, { className: "search-icon" })}
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="filter-buttons">
                <button
                  className={filter === 'ALL' ? 'active' : ''}
                  onClick={() => setFilter('ALL')}
                >
                  All
                </button>
                <button
                  className={filter === 'OPEN' ? 'active' : ''}
                  onClick={() => setFilter('OPEN')}
                >
                  Open
                </button>
                <button
                  className={filter === 'IN_PROGRESS' ? 'active' : ''}
                  onClick={() => setFilter('IN_PROGRESS')}
                >
                  In Progress
                </button>
                <button
                  className={filter === 'WAITING_FOR_CUSTOMER' ? 'active' : ''}
                  onClick={() => setFilter('WAITING_FOR_CUSTOMER')}
                >
                  Waiting for Customer
                </button>
                <button
                  className={filter === 'ESCALATED' ? 'active' : ''}
                  onClick={() => setFilter('ESCALATED')}
                >
                  Escalated
                </button>
                <button
                  className={filter === 'RESOLVED' ? 'active' : ''}
                  onClick={() => setFilter('RESOLVED')}
                >
                  Resolved
                </button>
                <button
                  className={filter === 'CLOSED' ? 'active' : ''}
                  onClick={() => setFilter('CLOSED')}
                >
                  Closed
                </button>
              </div>
              <div className="priority-filter">
                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                >
                  <option value="ALL">All Priorities</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="URGENT">Urgent</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
            </div>

            <div className="tickets-list-content">
              {filteredTickets.length === 0 ? (
                <div className="no-tickets-message">
                  <p>No tickets found</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Total tickets in stats: {stats?.totalTickets || 0}
                  </p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Tickets loaded from API: {tickets.length}
                  </p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    Current filters - Status: {filter}, Priority: {priorityFilter}
                  </p>
                  {(filter !== 'ALL' || priorityFilter !== 'ALL') && (
                    <div style={{ marginTop: '10px' }}>
                      <button
                        onClick={() => {
                          console.log('🔄 Resetting all filters...');
                          setFilter('ALL');
                          setPriorityFilter('ALL');
                          // Force reload after reset
                          setTimeout(() => {
                            loadTickets();
                          }, 100);
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#0080E0',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          marginRight: '8px'
                        }}
                      >
                        Reset All Filters
                      </button>
                      <button
                        onClick={() => {
                          console.log('🔄 Manually reloading tickets...');
                          loadTickets();
                          loadStats();
                        }}
                        style={{
                          padding: '8px 16px',
                          background: '#4caf50',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Reload Data
                      </button>
                      <p style={{ fontSize: '12px', color: '#ff9800', marginTop: '8px' }}>
                        ⚠️ Filters are active. Click above to reset and see all tickets.
                      </p>
                    </div>
                  )}
                  {tickets.length === 0 && stats && stats.totalTickets > 0 && (
                    <div style={{ marginTop: '10px', padding: '10px', background: '#fff3cd', borderRadius: '4px' }}>
                      <p style={{ fontSize: '12px', color: '#f44336', margin: '0 0 5px 0', fontWeight: 'bold' }}>
                        ⚠️ Stats show {stats.totalTickets} ticket(s) but API returned none.
                      </p>
                      <p style={{ fontSize: '11px', color: '#666', margin: '0' }}>
                        Possible causes:
                      </p>
                      <ul style={{ fontSize: '11px', color: '#666', margin: '5px 0 0 20px', padding: 0 }}>
                        <li>Ticket status doesn't match current filter ({filter})</li>
                        <li>Ticket priority doesn't match current filter ({priorityFilter})</li>
                        <li>API authentication issue</li>
                        <li>Backend filtering issue</li>
                      </ul>
                      <p style={{ fontSize: '11px', color: '#666', margin: '8px 0 0 0' }}>
                        💡 Check browser console (F12) for detailed API response logs.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  {filteredTickets
                    .map(ticket => {
                      // Additional safety check
                      if (!ticket) return null;

                      return (
                        <div
                          key={ticket.id}
                          className={`ticket-item ${(selectedTicket as any)?.id === ticket.id ? 'selected' : ''}`}
                          onClick={() => handleTicketClick(ticket)}
                        >
                          <div className="ticket-header">
                            <div className="ticket-id">#{ticket.id}</div>
                            <div className="ticket-priority" style={{ color: '#000' }}>
                              {ticket.priority}
                            </div>
                          </div>
                          <div className="ticket-subject">{ticket.subject}</div>
                          <div className="ticket-meta">
                            <span className="ticket-user">
                              {ticket.user && ticket.user.name ? ticket.user.name : resolveTicketUser(ticket).name}
                            </span>
                            <span
                              className="ticket-status"
                              style={{
                                color: getStatusColor(ticket.status),
                                fontWeight: 'bold'
                              }}
                            >
                              {ticket.status.replace(/_/g, ' ')}
                            </span>
                          </div>
                          <div className="ticket-date">
                            {new Date(ticket.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      );
                    })
                    .filter(Boolean) // Remove any null values
                  }
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Manual Ticket Modal */}
      {showManualModal && (
        <div className="modal-overlay">
          <div className="manual-ticket-modal">
            <div className="modal-header">
              <h3>Create Manual Support Ticket</h3>
              <button className="close-btn" onClick={() => setShowManualModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateManualTicket}>
              <div className="modal-body">
                <div className="form-group row">
                  <div className="col">
                    <label>User ID (Existing User)</label>
                    <input
                      type="number"
                      placeholder="Enter User ID"
                      value={manualTicketData.userId || ''}
                      onChange={(e) => setManualTicketData({ ...manualTicketData, userId: e.target.value ? parseInt(e.target.value) : undefined })}
                    />
                  </div>
                  <div className="col">
                    <label>Phone Number (Guest/New)</label>
                    <input
                      type="text"
                      placeholder="+966..."
                      value={manualTicketData.phoneNumber || ''}
                      onChange={(e) => setManualTicketData({ ...manualTicketData, phoneNumber: e.target.value })}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>User Name (Optional for Guests)</label>
                  <input
                    type="text"
                    placeholder="e.g. Guest John"
                    value={manualTicketData.userName || ''}
                    onChange={(e) => setManualTicketData({ ...manualTicketData, userName: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Subject*</label>
                  <input
                    type="text"
                    required
                    placeholder="Ticket Subject"
                    value={manualTicketData.subject}
                    onChange={(e) => setManualTicketData({ ...manualTicketData, subject: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Description*</label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Describe the issue..."
                    value={manualTicketData.description}
                    onChange={(e) => setManualTicketData({ ...manualTicketData, description: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={manualTicketData.priority}
                    onChange={(e) => setManualTicketData({ ...manualTicketData, priority: e.target.value })}
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                    <option value="CRITICAL">Critical</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowManualModal(false)}>Cancel</button>
                <button type="submit" className="btn-save" disabled={creatingManual}>
                  {creatingManual ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;

